import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-8 md:gap-10">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        {testimonial ? (
          <figure className="grid gap-3 overflow-hidden rounded-[28px] bg-lilac p-3 text-ink md:grid-cols-[minmax(16rem,0.85fr)_1.15fr]">
            {testimonial.portrait ? (
              <DaylightMedia
                className="aspect-4/3 min-w-0 rounded-[22px] md:aspect-4/5"
                imageClassName="object-cover"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 38vw, calc(100vw - 48px)"
              />
            ) : null}
            <div
              className={cn(
                "flex min-w-0 flex-col justify-center rounded-[22px] bg-daylight/75 p-7 md:p-10 lg:p-12",
                !testimonial.portrait && "md:col-span-full",
              )}
            >
              <span aria-hidden="true" className="mb-5 block h-1 w-10 bg-cobalt" />
              <blockquote>
                <DaylightRichText
                  className="font-daylight-display text-2xl font-semibold leading-[1.12] tracking-[-0.025em] text-ink md:text-3xl lg:text-4xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-8 border-t border-ink/15 pt-5">
                <p className="font-daylight-body text-sm font-semibold">{testimonial.name}</p>
                {testimonial.attribution ? (
                  <p className="mt-1 font-daylight-body text-sm text-slate">
                    {testimonial.attribution}
                  </p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="mt-5 inline-flex min-h-11 items-center self-start font-daylight-body text-sm font-semibold text-cobalt underline decoration-cobalt/40 underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </DaylightSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
